import { useState } from 'react';
import { useLocation } from 'wouter';
import { useTheme } from 'next-themes';
import { useCreateChatRoom } from '@workspace/api-client-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LangToggle } from '@/components/ui/LangToggle';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLang } from '@/lib/i18n';
import { generateRoomSecret, withRoomSecret, getRememberedRoomSecret, rememberRoomSecret } from '@/lib/roomCrypto';

export function Home() {
  const [, setLocation] = useLocation();
  const { t } = useLang();
  
  const { resolvedTheme } = useTheme();
  const logo = resolvedTheme === "onyx" ? "/room-purple.svg" : "/room-blue.svg";

  const searchParams = new URLSearchParams(window.location.search);
  const isJoinFlow = searchParams.get('join') === 'true';
  const prefillRoomId = searchParams.get('room') || '';

  const [roomCode, setRoomCode] = useState(prefillRoomId);
  const [showJoinForm, setShowJoinForm] = useState(isJoinFlow || !!prefillRoomId);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [name, setName] = useState('');
  const [pendingRoomId, setPendingRoomId] = useState<string | null>(null);
  const [pendingRoomSecret, setPendingRoomSecret] = useState<string | null>(null);

  const { mutate: createRoom, isPending: isCreating } = useCreateChatRoom();

  const handleCreate = () => {
    const roomSecret = generateRoomSecret();

    createRoom(undefined, {
      onSuccess: (data) => {
        const storedName = sessionStorage.getItem('tobius-chat-name');
        if (!storedName) {
          rememberRoomSecret(data.room_id, roomSecret);
          setPendingRoomId(data.room_id);
          setPendingRoomSecret(roomSecret);
          setShowNameDialog(true);
        } else {
          rememberRoomSecret(data.room_id, roomSecret);
          setLocation(withRoomSecret(`/room/${data.room_id}`, roomSecret));
        }
      }
    });
  };

  const parseInvite = (value: string) => {
    const trimmed = value.trim();
    try {
      const url = new URL(trimmed, window.location.origin);
      if (url.pathname.startsWith('/room/')) {
        return {
          roomId: decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || ''),
          secret: url.hash ? getRoomSecretFromHash(url.hash) : null,
        };
      }
    } catch {
      // Plain room code.
    }
    return { roomId: trimmed.replace(/^\/room\//, '').replace(/^\//, ''), secret: null as string | null };
  };

  const getRoomSecretFromHash = (hash: string): string | null => {
    try {
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const key = params.get('k');
      return key && key.length >= 40 ? key : null;
    } catch {
      return null;
    }
  };

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomCode.trim()) return;

    const { roomId, secret } = parseInvite(roomCode);
    if (!roomId) return;

    const rememberedSecret = secret || getRememberedRoomSecret(roomId);
    if (secret) rememberRoomSecret(roomId, secret);

    const storedName = sessionStorage.getItem('tobius-chat-name');
    if (!storedName) {
      setPendingRoomId(roomId);
      setPendingRoomSecret(rememberedSecret);
      setShowNameDialog(true);
      return;
    }

    if (rememberedSecret) {
      setLocation(withRoomSecret(`/room/${roomId}`, rememberedSecret));
    } else {
      setLocation(`/room/${roomId}`);
    }
  };

  const handleSaveNameAndProceed = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    sessionStorage.setItem('tobius-chat-name', name.trim());
    setShowNameDialog(false);
    if (pendingRoomId) {
      setLocation(pendingRoomSecret ? withRoomSecret(`/room/${pendingRoomId}`, pendingRoomSecret) : `/room/${pendingRoomId}`);
    }
    setPendingRoomId(null);
    setPendingRoomSecret(null);
  };

  return (
    <div className="min-h-[100dvh] w-full flex flex-col relative text-foreground overflow-x-hidden px-4 pb-6 pt-[calc(1rem+env(safe-area-inset-top))] sm:px-6">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1 sm:top-4 sm:right-4">
        <LangToggle />
        <ThemeToggle />
      </div>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-6 w-full mx-auto">
        <div className="flex flex-col items-center w-full animate-in fade-in zoom-in-95 duration-700 ease-out">
          
          {/* Logo - Gestion explicite des largeurs par breakpoints */}
          <div className="mb-8 flex w-full justify-center">
            <img
              src={logo}
              alt="ROOM"
              draggable={false}
              className="
                block
                h-auto
                w-[340px]
                sm:w-[420px]
                md:w-[520px]
                lg:w-[620px]
                xl:w-[700px]
                max-w-full
                select-none
                pointer-events-none
                transition-all
                duration-300
              "
            />
          </div>

          {/* Carte indépendante et centrée */}
          <div className="w-full max-w-md space-y-8 flex flex-col items-center bg-card/40 backdrop-blur-xl p-5 sm:p-8 rounded-2xl border border-border/50 shadow-xl">
            <Button
              size="lg"
              className="w-full text-lg min-h-12 rounded-xl shadow-md transition-all hover:shadow-lg active:scale-[0.98]"
              onClick={handleCreate}
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('home.creating')}
                </>
              ) : (
                t('home.createRoom')
              )}
            </Button>

            <div className="w-full flex items-center gap-4">
              <div className="h-px bg-border/60 flex-1"></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">{t('home.or')}</span>
              <div className="h-px bg-border/60 flex-1"></div>
            </div>

            {!showJoinForm ? (
              <button
                onClick={() => setShowJoinForm(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
              >
                {t('home.haveLink')}
              </button>
            ) : (
              <form onSubmit={handleJoin} className="w-full flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <label htmlFor="roomCode" className="text-sm font-medium text-muted-foreground">
                  {t('home.enterCode')}
                </label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    id="roomCode"
                    value={roomCode}
                    onChange={(e) => setRoomCode(e.target.value)}
                    placeholder={t('home.codePlaceholder')}
                    className="min-h-12 bg-background/50 backdrop-blur-sm focus-visible:ring-1"
                    autoComplete="off"
                    autoFocus
                  />
                  <Button type="submit" variant="secondary" className="min-h-12 px-6">
                    {t('home.join')}
                  </Button>
                </div>
              </form>
            )}
          </div>

        </div>
      </main>

      {/* Bandeau sécurité : uniquement sur la page d'accueil ROOM */}
      <div className="room-alarm-ticker" role="status" aria-label="Information de sécurité">
        <div className="room-alarm-ticker__viewport">
          <div className="room-alarm-ticker__track">
            <span className="room-alarm-ticker__message">
              🔒 CHIFFRÉ DE BOUT EN BOUT&nbsp;&nbsp;•&nbsp;&nbsp;🗑️ DONNÉES UTILISATEUR SUPPRIMÉES AUTOMATIQUEMENT APRÈS 30 MIN&nbsp;&nbsp;•&nbsp;&nbsp;🛡️ PSEUDOS LIBRES : UTILISEZ UN SAFE WORD&nbsp;&nbsp;•&nbsp;&nbsp;QUESTION → RÉPONSE
            </span>
            <span className="room-alarm-ticker__message" aria-hidden="true">
              🔒 CHIFFRÉ DE BOUT EN BOUT&nbsp;&nbsp;•&nbsp;&nbsp;🗑️ DONNÉES UTILISATEUR SUPPRIMÉES AUTOMATIQUEMENT APRÈS 30 MIN&nbsp;&nbsp;•&nbsp;&nbsp;🛡️ PSEUDOS LIBRES : UTILISEZ UN SAFE WORD&nbsp;&nbsp;•&nbsp;&nbsp;QUESTION → RÉPONSE
            </span>
          </div>
        </div>
      </div>

      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent 
          className="sm:max-w-md backdrop-blur-2xl bg-card border-border shadow-2xl [&>button]:hidden" 
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="pt-2">
            <DialogTitle className="text-2xl font-light text-center mb-1">{t('home.whoAreYou')}</DialogTitle>
            <DialogDescription className="text-center text-muted-foreground">
              {t('home.nameDesc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveNameAndProceed} className="space-y-6 py-4">
            <div className="space-y-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('home.namePlaceholder')}
                className="min-h-12 bg-background/50 text-center text-lg"
                autoFocus
                autoComplete="off"
                maxLength={30}
              />
            </div>
            <Button type="submit" className="w-full min-h-12 text-md" disabled={!name.trim()}>
              {t('home.joinRoom')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}